import { useState } from 'react';
import { ChevronDown, ChevronRight, CheckCircle2, Clock, AlertCircle, Zap } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  developmentPhases,
  calculateOverallProgress,
  getNextPrioritizedTasks,
  type Phase,
  type Task,
  type TaskStatus,
} from '@/data/development-progress';

const statusConfig: Record<TaskStatus, { icon: React.ReactNode; color: string; label: string }> = {
  completed: {
    icon: <CheckCircle2 className="w-4 h-4" />,
    color: 'bg-green-100 text-green-800 border-green-300',
    label: 'Concluído',
  },
  'in-progress': {
    icon: <Zap className="w-4 h-4" />,
    color: 'bg-blue-100 text-blue-800 border-blue-300',
    label: 'Em Progresso',
  },
  pending: {
    icon: <Clock className="w-4 h-4" />,
    color: 'bg-gray-100 text-gray-800 border-gray-300',
    label: 'Pendente',
  },
  blocked: {
    icon: <AlertCircle className="w-4 h-4" />,
    color: 'bg-red-100 text-red-800 border-red-300',
    label: 'Bloqueado',
  },
};

interface TaskItemProps {
  task: Task;
  isExpanded: boolean;
  onToggle: () => void;
}

function TaskItem({ task, isExpanded, onToggle }: TaskItemProps) {
  const config = statusConfig[task.status];

  return (
    <div className="border-l-2 border-gray-200 pl-4 py-3">
      <div className="flex items-start gap-3">
        <button
          onClick={onToggle}
          className="mt-1 p-0 hover:bg-gray-100 rounded transition-colors"
        >
          {task.subtasks && task.subtasks.length > 0 ? (
            isExpanded ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )
          ) : (
            <div className="w-4 h-4" />
          )}
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <span className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium border ${config.color}`}>
              {config.icon}
              {config.label}
            </span>
            {task.estimatedDays && (
              <span className="text-xs text-gray-500">~{task.estimatedDays}d</span>
            )}
          </div>

          <h4 className="font-semibold text-sm text-gray-900 mb-1">{task.title}</h4>
          <p className="text-xs text-gray-600 mb-2">{task.description}</p>

          {/* Progress Bar */}
          <div className="mb-2">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-gray-500">Progresso</span>
              <span className="text-xs font-medium text-gray-700">{task.progress}%</span>
            </div>
            <Progress value={task.progress} className="h-1.5" />
          </div>

          {/* Next Steps */}
          {isExpanded && task.nextSteps && task.nextSteps.length > 0 && (
            <div className="mt-3 pt-3 border-t border-gray-200">
              <p className="text-xs font-semibold text-gray-700 mb-2">Próximos Passos:</p>
              <ul className="space-y-1">
                {task.nextSteps.map((step, idx) => (
                  <li key={idx} className="text-xs text-gray-600 flex gap-2">
                    <span className="text-gray-400">•</span>
                    <span>{step}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Subtasks */}
      {isExpanded && task.subtasks && task.subtasks.length > 0 && (
        <div className="mt-3 ml-6 space-y-2">
          {task.subtasks.map((subtask) => (
            <div key={subtask.id} className="text-xs text-gray-600 flex gap-2">
              <span className="text-gray-400">→</span>
              <span>{subtask.title}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

interface PhaseCardProps {
  phase: Phase;
  isExpanded: boolean;
  onToggle: () => void;
}

function PhaseCard({ phase, isExpanded, onToggle }: PhaseCardProps) {
  const [expandedTasks, setExpandedTasks] = useState<Set<string>>(new Set());
  const config = statusConfig[phase.status];
  const completedTasks = phase.tasks.filter((t) => t.status === 'completed').length;

  const toggleTask = (taskId: string) => {
    const newSet = new Set(expandedTasks);
    if (newSet.has(taskId)) {
      newSet.delete(taskId);
    } else {
      newSet.add(taskId);
    }
    setExpandedTasks(newSet);
  };

  return (
    <Card className="overflow-hidden border-l-4" style={{ borderLeftColor: phase.status === 'completed' ? '#10b981' : phase.status === 'in-progress' ? '#3b82f6' : '#d1d5db' }}>
      <div className="p-4">
        {/* Phase Header */}
        <button
          onClick={onToggle}
          className="w-full text-left flex items-start gap-3 hover:opacity-80 transition-opacity"
        >
          <div className="mt-1">
            {isExpanded ? (
              <ChevronDown className="w-5 h-5 text-gray-600" />
            ) : (
              <ChevronRight className="w-5 h-5 text-gray-600" />
            )}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <h3 className="font-bold text-lg text-gray-900">
                {phase.order}. {phase.name}
              </h3>
              <span className={`flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium border ${config.color}`}>
                {config.icon}
                {config.label}
              </span>
            </div>

            <p className="text-sm text-gray-600 mb-3">{phase.description}</p>

            {/* Progress Bar */}
            <div className="mb-2">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-gray-500">
                  {completedTasks} de {phase.tasks.length} tarefas concluídas
                </span>
                <span className="text-xs font-medium text-gray-700">{phase.progress}%</span>
              </div>
              <Progress value={phase.progress} className="h-2" />
            </div>
          </div>
        </button>

        {/* Tasks List */}
        {isExpanded && (
          <div className="mt-4 pt-4 border-t border-gray-200 space-y-3">
            {phase.tasks.map((task) => (
              <TaskItem
                key={task.id}
                task={task}
                isExpanded={expandedTasks.has(task.id)}
                onToggle={() => toggleTask(task.id)}
              />
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}

export function DevelopmentDashboard() {
  const [expandedPhases, setExpandedPhases] = useState<Set<string>>(
    new Set(['phase-4']) // Expandir fase atual por padrão
  );
  const overallProgress = calculateOverallProgress();
  const nextTasks = getNextPrioritizedTasks(5);

  const togglePhase = (phaseId: string) => {
    const newSet = new Set(expandedPhases);
    if (newSet.has(phaseId)) {
      newSet.delete(phaseId);
    } else {
      newSet.add(phaseId);
    }
    setExpandedPhases(newSet);
  };

  return (
    <div className="space-y-6">
      {/* Overall Progress */}
      <Card className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Progresso Geral do Projeto</h2>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">Conclusão Total</span>
            <span className="text-2xl font-bold text-blue-600">{overallProgress}%</span>
          </div>
          <Progress value={overallProgress} className="h-3" />
          <p className="text-xs text-gray-600 mt-2">
            Acompanhe o progresso de cada fase e tarefa abaixo. Clique para expandir e ver os próximos passos.
          </p>
        </div>
      </Card>

      {/* Next Priority Tasks */}
      <Card className="p-6 border-amber-200 bg-amber-50">
        <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Zap className="w-5 h-5 text-amber-600" />
          Próximas Tarefas Prioritárias
        </h3>
        <div className="space-y-3">
          {nextTasks.map((task) => {
            const config = statusConfig[task.status];
            return (
              <div key={task.id} className="flex items-start gap-3 p-3 bg-white rounded-lg border border-amber-100">
                <span className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium border ${config.color} flex-shrink-0`}>
                  {config.icon}
                  {config.label}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm text-gray-900">{task.title}</p>
                  <p className="text-xs text-gray-600">{task.description}</p>
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Phases */}
      <div className="space-y-4">
        <h2 className="text-xl font-bold text-gray-900">Fases do Desenvolvimento</h2>
        {developmentPhases.map((phase) => (
          <PhaseCard
            key={phase.id}
            phase={phase}
            isExpanded={expandedPhases.has(phase.id)}
            onToggle={() => togglePhase(phase.id)}
          />
        ))}
      </div>
    </div>
  );
}
